import { useEffect, useState } from "react";
import { money } from "../api.js";
import { useResource } from "./useResource.js";
import { Empty } from "./common.jsx";

// Довідник УКТЗЕД (Митний тариф, як uktzed.db etax) і ДКПП — за класифікацією продукції Держстату
// (СКП, до рівня XX.XX.XX: повного ДК 016:2010 у відкритих даних немає)
export const fmtUktzed = (c) => { const s = String(c || ""); return [s.slice(0, 4), s.slice(4, 6), s.slice(6, 8), s.slice(8, 10)].filter(Boolean).join(" "); };
const clean = (s) => String(s || "").replace(/^(-\s*)+/, "").replace(/:$/, "").trim();
const indent = (s) => (String(s).match(/^(-\s*)+/)?.[0].replace(/\s/g, "").length || 0);

export default function Reference({ companyId, sub, versions }) {
  const [kind, ...rest] = (sub || "").split("/");
  const code = rest.join("/");
  return (
    <>
      <div className="section-head"><div><h2>Довідник УКТЗЕД / ДКПП</h2>
        <p className="notice">УКТЗЕД — Митний тариф України. ДКПП — Статистична класифікація продукції Держстату (до рівня XX.XX.XX); повного ДК 016:2010 у машинозчитуваному вигляді у відкритих даних немає.</p></div></div>
      <div className={`ref-layout${sub ? " has-detail" : ""}`}>
        <SearchPanel companyId={companyId} versions={versions} selected={sub} />
        {sub ? <Detail companyId={companyId} kind={kind} code={code} versions={versions} /> : <div className="card ref-placeholder"><Empty>Оберіть код зі списку або знайдіть за кодом чи назвою.</Empty></div>}
      </div>
    </>
  );
}

function SearchPanel({ companyId, versions, selected }) {
  const [q, setQ] = useState("");
  const [deb, setDeb] = useState("");
  useEffect(() => { const t = setTimeout(() => setDeb(q.trim()), 200); return () => clearTimeout(t); }, [q]);
  const s = useResource(deb.length >= 2 ? `/api/ref/search?q=${encodeURIComponent(deb)}` : null, deb);
  const mine = useResource(`/api/companies/${companyId}/movement`, versions.erpn);
  const link = (k, c) => `#/c/${companyId}/reference/${k}/${encodeURIComponent(c)}`;
  const first = () => {
    const u = s.data?.uktzed?.[0], d = s.data?.dkpp?.[0];
    if (u) location.hash = link("uktzed", u.code); else if (d) location.hash = link("dkpp", d.code);
  };
  const Item = ({ k, c, title, meta }) => (
    <li><a href={link(k, c)} className={selected === `${k}/${c}` ? "active" : ""} aria-current={selected === `${k}/${c}` ? "true" : undefined}>
      <span className="mono-sm ref-code">{k === "uktzed" ? fmtUktzed(c) : c}</span><span className="ref-title">{title}</span>{meta && <span className="muted small">{meta}</span>}
    </a></li>
  );
  return (
    <section className="card ref-search" aria-label="Пошук у довіднику">
      <input type="search" className="search-wide" aria-label="Код або назва" placeholder="Код (8536…, 43.29) або назва" value={q}
        onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") first(); }} />
      {deb.length < 2 ? (
        <>
          <h3 className="mini-title">Мої коди — з ПН/РК і МД компанії</h3>
          {mine.pending ? <p className="muted" role="status">Завантаження…</p> : !mine.data?.rows?.length ? <Empty>Кодів у даних компанії ще немає.</Empty> : (
            <ul className="ref-list">{mine.data.rows.slice(0, 60).map((x) => <Item key={x.code} k={x.kind} c={x.code} title={x.name} meta={`${money(x.turnover)} грн`} />)}</ul>
          )}
        </>
      ) : s.pending || s.updating ? <p className="muted" role="status">Шукаємо…</p>
        : s.error ? <p className="error">{s.error.message}</p> : (
          <>
            {!s.data.uktzed.length && !s.data.dkpp.length && <Empty>Нічого не знайдено.</Empty>}
            {s.data.uktzed.length > 0 && <><h3 className="mini-title">УКТЗЕД: {s.data.uktzed.length}{s.data.uktzed.length >= 150 ? "+" : ""}</h3>
              <ul className="ref-list">{s.data.uktzed.map((x) => <Item key={x.code} k="uktzed" c={x.code} title={x.short} meta={x.is_leaf ? (x.rate_priv ? `мито ${x.rate_priv} %` : null) : "група"} />)}</ul></>}
            {s.data.dkpp.length > 0 && <><h3 className="mini-title">ДКПП (СКП): {s.data.dkpp.length}{s.data.dkpp.length >= 150 ? "+" : ""}</h3>
              <ul className="ref-list">{s.data.dkpp.map((x) => <Item key={x.code} k="dkpp" c={x.code} title={x.name} />)}</ul></>}
          </>
        )}
    </section>
  );
}

function Detail({ companyId, kind, code, versions }) {
  const r = useResource(`/api/ref/${kind}/${encodeURIComponent(code)}`);
  const mine = useResource(`/api/companies/${companyId}/movement`, versions.erpn);
  const my = mine.data?.rows?.find((x) => x.code === code);
  const [copied, setCopied] = useState(false);
  const link = (k, c) => `#/c/${companyId}/reference/${k}/${encodeURIComponent(c)}`;
  if (r.pending) return <div className="card"><p className="muted" role="status">Завантаження…</p></div>;
  if (r.error) return <div className="card"><p className="crumbs"><a href={`#/c/${companyId}/reference`}>← До пошуку</a></p><p className="error">{r.error.message}</p>{my && <Mine my={my} />}</div>;
  const { ref, related, relation, exact } = r.data;
  const path = Array.isArray(ref.path) ? ref.path : [];
  const title = kind === "uktzed" ? clean(path.at(-1) || ref.name) : ref.name;
  const copy = async () => { try { await navigator.clipboard.writeText(ref.code); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch {} };
  return (
    <section className="card ref-detail" aria-labelledby="ref-h">
      <p className="crumbs ref-back"><a href={`#/c/${companyId}/reference`}>← До пошуку</a></p>
      <div className="row">
        <h3 id="ref-h" className="ref-big mono-sm">{kind === "uktzed" ? fmtUktzed(ref.code) : ref.code}</h3>
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>{copied ? "Скопійовано" : "Копіювати код"}</button>
      </div>
      <p className="ref-name">{title}</p>
      {!exact && <p className="notice">Код {code} у довіднику не знайдено — показано найближчий батьківський {kind === "uktzed" ? "код" : "рівень"} {ref.code}.</p>}
      <div className="chips">
        {kind === "uktzed" && ref.rate_priv != null && <span className="tag">Пільгова: {ref.rate_priv} %</span>}
        {kind === "uktzed" && ref.rate_full != null && <span className="tag">Повна: {ref.rate_full} %</span>}
        {kind === "uktzed" && ref.unit && <span className="tag">Дод. од.: {ref.unit}</span>}
        {kind === "uktzed" && <span className={ref.risky ? "tag tag-warn" : "tag"}>{ref.risky ? "У переліку ДПС № 67" : "Поза переліком ДПС № 67"}</span>}
        {kind === "dkpp" && <span className="tag">{({ section: "Секція", division: "Розділ", group: "Група", class: "Клас", category: "Категорія", subcategory: "Підкатегорія" })[ref.level]}</span>}
      </div>
      {path.length > 0 && (
        <>
          <h4>Класифікація</h4>
          <ol className="ref-path">
            {path.map((l, i) => <li key={i} style={{ "--ind": kind === "uktzed" ? indent(l) + Math.min(i, 2) : i }} className={i < 2 ? "strong" : ""}>{clean(l)}</li>)}
          </ol>
        </>
      )}
      {my && <Mine my={my} />}
      {related?.length > 0 && (
        <>
          <h4>{relation === "children" ? "Вкладені коди" : "Сусідні коди гілки"}</h4>
          <ul className="ref-list ref-related">
            {related.slice(0, 30).map((x) => <li key={x.code}><a href={link(kind, x.code)}><span className="mono-sm ref-code">{kind === "uktzed" ? fmtUktzed(x.code) : x.code}</span><span className="ref-title">{x.short || x.name}</span></a></li>)}
          </ul>
          {related.length > 30 && <p className="muted small">… і ще {related.length - 30}</p>}
        </>
      )}
      {r.data.source && <p className="notice">{r.data.source}</p>}
    </section>
  );
}

// Кількість за кожною одиницею окремо (кг і л одного коду не складаємо)
const qty = (my, f) => (my.units?.length ? my.units : [{ unit: my.unit, [f]: my[f] ?? 0 }])
  .map((u) => `${Number(u[f] || 0).toLocaleString("uk-UA", { maximumFractionDigits: 3 })}${u.unit ? ` ${u.unit}` : ""}`).join(" · ");

function Mine({ my }) {
  return (
    <>
      <h4>У ваших даних</h4>
      <dl className="facts">
        <div><dt>Надходження</dt><dd>{qty(my, "inflowQty")} / {money(my.inValue)} грн</dd></div>
        <div><dt>Вибуття</dt><dd>{qty(my, "qtyOut")} / {money(my.outValue)} грн</dd></div>
        <div><dt>Залишок</dt><dd>{qty(my, "balanceQty")} / {money(my.balance)} грн без ПДВ, {money(my.balanceVat)} грн з ПДВ</dd></div>
      </dl>
    </>
  );
}
